import { useCallback, useRef, useState } from 'react';
import { UploadCloud, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTH_TOKEN_KEY = 'alivan-auth-token';
const authHeaders = () => {
  try {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch (_) { return {}; }
};

const ACCEPT = '.mp4,.mov,.webm,.mp3,.wav,.jpg,.jpeg,.png';
const HUMAN_ACCEPT = 'MP4, MOV, WebM, MP3, WAV, JPG, PNG';
const MAX_MB = 200;

const humanSize = (b) => {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const UploadZone = ({ jobId, sourceCount = 0, maxFiles = 8, onUploaded, onError }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [queue, setQueue] = useState([]); // {name, size}
  const [rejects, setRejects] = useState([]);
  const inputRef = useRef(null);

  const slotsLeft = Math.max(0, maxFiles - sourceCount);

  const onPick = () => inputRef.current?.click();

  const handleFiles = useCallback(async (files) => {
    if (!jobId) {
      onError?.('Primero crea o selecciona un proyecto.');
      return;
    }
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    if (arr.length > slotsLeft) {
      onError?.(`Sólo quedan ${slotsLeft} espacio(s) en este proyecto.`);
      return;
    }
    const oversized = arr.filter(f => f.size > MAX_MB * 1024 * 1024);
    if (oversized.length) {
      onError?.(`Cada archivo debe pesar ≤ ${MAX_MB} MB (excede: ${oversized.map(f => f.name).join(', ')})`);
      return;
    }
    setQueue(arr.map(f => ({ name: f.name, size: f.size })));
    setRejects([]);
    setProgress(0);
    setUploading(true);

    const fd = new FormData();
    for (const f of arr) fd.append('files', f, f.name);

    // Use XHR to get upload progress
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/production/projects/${jobId}/upload`, true);
        xhr.withCredentials = true;
        const hdrs = authHeaders();
        Object.keys(hdrs).forEach(k => xhr.setRequestHeader(k, hdrs[k]));
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try {
            const body = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300) resolve(body);
            else reject(body?.detail || `HTTP ${xhr.status}`);
          } catch (_) {
            reject(`HTTP ${xhr.status}`);
          }
        };
        xhr.onerror = () => reject('network_error');
        xhr.send(fd);
      });
      if (result.rejected?.length) setRejects(result.rejected);
      onUploaded?.(result);
    } catch (e) {
      onError?.(typeof e === 'string' ? e : (e?.detail || 'La subida falló'));
    } finally {
      setUploading(false);
      setQueue([]);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [jobId, slotsLeft, onUploaded, onError]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="prod-upload-zone">
      <div
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={uploading ? undefined : onPick}
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
          dragging ? 'border-neon-red bg-neon-red/10' : 'border-neon-red/30 bg-black/30 hover:border-neon-red/60'
        } ${uploading ? 'opacity-70 cursor-wait' : ''}`}
        data-testid="prod-upload-dropzone"
      >
        <UploadCloud className="w-8 h-8 mx-auto text-neon-red/70 mb-2" />
        <div className="text-sm text-white/90 font-outfit">
          Arrastra clips aquí o <span className="text-neon-red underline">selecciona archivos</span>
        </div>
        <div className="text-[10px] text-white/50 font-jetbrains mt-1 uppercase tracking-widest">
          {HUMAN_ACCEPT} · máx {MAX_MB} MB c/u · {slotsLeft} espacio(s) libres de {maxFiles}
        </div>
        <input
          ref={inputRef}
          data-testid="prod-upload-input"
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {uploading && (
        <div className="mt-3" data-testid="prod-upload-progress">
          <div className="flex items-center gap-2 text-[11px] text-white/70">
            <Loader2 className="w-3 h-3 animate-spin text-neon-red" />
            Subiendo {queue.length} archivo(s)… {progress}%
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded mt-1 overflow-hidden">
            <div className="h-full bg-neon-red transition-all" style={{ width: `${progress}%` }} />
          </div>
          <ul className="mt-2 text-[10px] text-white/50 space-y-0.5">
            {queue.map((q, i) => (
              <li key={i}>· {q.name} <span className="text-white/30">({humanSize(q.size)})</span></li>
            ))}
          </ul>
        </div>
      )}

      {rejects.length > 0 && (
        <div className="mt-3 border border-yellow-500/40 bg-yellow-500/10 rounded p-2 text-[11px] text-yellow-200" data-testid="prod-upload-rejects">
          <div className="font-jetbrains uppercase text-[10px] mb-1">Archivos rechazados</div>
          <ul>
            {rejects.map((r, i) => (
              <li key={i}>· <span className="font-mono">{r.filename}</span> — {r.reason}</li>
            ))}
          </ul>
          <button
            onClick={() => setRejects([])}
            className="mt-1 text-[10px] text-yellow-100/70 underline"
            data-testid="prod-upload-rejects-clear"
          >
            Ocultar
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadZone;
